import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  AccountBalance,
  Add as AddIcon,
  Edit,
  Delete,
  ContentCopy,
  Print,
  Search,
  FilterList,
  Close
} from '@mui/icons-material';
import { employeeService } from '../services/employeeService';
import { useSearchParams } from 'react-router-dom';
import { formatDateForInput, formatDateDisplay, parseLocalDate, formatLocalTodayYmd } from '../utils/dateUtils';
import { formatCurrency } from '../components/CurrencyTextField';
import { useAuth } from '../hooks/useAuth';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import ReportExportMenu from '../components/ReportExportMenu';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';

const HR_PAY_RATE_COLUMN_KEY = 'hr.showPayRateColumn';

const PAY_RATE_TYPES = ['REGULAR', '2ND_SHIFT', '3RD_SHIFT', 'VACATION'];

const formatNinetyDayDate = (dateOfHire) => {
  const date = parseLocalDate(formatDateForInput(dateOfHire));
  if (!date) return '—';
  date.setDate(date.getDate() + 90);
  return formatDateDisplay(date);
};

/** Full roster for print/export: no filters, paginated until exhausted. */
async function fetchAllEmployeesUnfiltered() {
  const pageSize = 500;
  const all = [];
  let offset = 0;
  for (;;) {
    const response = await employeeService.getAll({
      limit: pageSize,
      offset,
      activeOnly: ''
    });
    const batch = response.data ?? [];
    all.push(...batch);
    if (batch.length < pageSize) break;
    offset += pageSize;
  }
  return all;
}

const emptyForm = () => ({
  employee_number: '',
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  phone: '',
  role: 'employee',
  is_active: true,
  date_of_hire: '',
  job_title: '',
  department: '',
  current_pay_rate: '',
  kiosk_pin: '',
  supervisor_id: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  requires_training: true,
  data_collection: false,
  scoped_can_use_existing_customers: true,
  scoped_see_others_documents: false
});

const HumanResources = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const focusEmployeeIdRaw = searchParams.get('employee_id');
  const focusEmployeeId =
    focusEmployeeIdRaw != null && String(focusEmployeeIdRaw).trim() !== ''
      ? parseInt(String(focusEmployeeIdRaw).trim(), 10)
      : null;
  const focusEmployeeIdValid = Number.isFinite(focusEmployeeId) ? focusEmployeeId : null;
  const didApplyFocusFromUrl = useRef(false);
  const urlJobTitle = (searchParams.get('job_title') || '').trim();
  const urlDepartment = (searchParams.get('department') || '').trim();
  const canManageEnterpriseAdmins = user?.role === 'enterprise_admin';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('new'); // 'new' | 'edit' | 'copy'
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeFilter, setActiveFilter] = useState('active'); // 'active' | 'all'
  const [departmentFilter, setDepartmentFilter] = useState(urlDepartment);
  const [jobTitleFilter, setJobTitleFilter] = useState(urlJobTitle);
  const [hireDateFrom, setHireDateFrom] = useState('');
  const [hireDateTo, setHireDateTo] = useState('');
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  /** When non-null, a print-only roster is mounted (all employees). Cleared after print. */
  const [employeesForPrintList, setEmployeesForPrintList] = useState(null);
  const [printListLoading, setPrintListLoading] = useState(false);
  const [showPayRateColumn, setShowPayRateColumn] = useState(() => {
    try {
      const v = localStorage.getItem(HR_PAY_RATE_COLUMN_KEY);
      if (v === null) return false;
      return v === '1';
    } catch {
      return false;
    }
  });
  const [historyDialog, setHistoryDialog] = useState(null); // 'pay' | 'work' | 'note' | null
  const [historySaving, setHistorySaving] = useState(false);
  const [payHistoryForm, setPayHistoryForm] = useState({
    effective_date: '',
    rate: '',
    rate_type: 'REGULAR'
  });
  const [workHistoryForm, setWorkHistoryForm] = useState({
    effective_date: '',
    notes: ''
  });
  const [noteForm, setNoteForm] = useState({
    note_date: '',
    note_text: ''
  });

  const employeeTableColCount = showPayRateColumn ? 8 : 7;

  const loadEmployees = async (overrides = {}) => {
    try {
      setLoading(true);
      setError('');
      const params = {
        search: overrides.search !== undefined ? overrides.search || undefined : (searchTerm || undefined),
        activeOnly: activeFilter === 'active' ? 'true' : '',
        department:
          overrides.department !== undefined
            ? overrides.department || undefined
            : (departmentFilter?.trim() || undefined),
        job_title:
          overrides.job_title !== undefined
            ? overrides.job_title || undefined
            : (jobTitleFilter?.trim() || undefined),
        hireDateFrom: hireDateFrom || undefined,
        hireDateTo: hireDateTo || undefined,
        limit: 500
      };
      const response = await employeeService.getAll(params);
      setEmployees(response.data ?? []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError(err.response?.data?.message || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, [activeFilter]);

  // Deep-link filters from training assignment chips (and similar): /hr?job_title=…&department=…
  useEffect(() => {
    const jt = (searchParams.get('job_title') || '').trim();
    const dept = (searchParams.get('department') || '').trim();
    if (!jt && !dept) return;
    setJobTitleFilter(jt);
    setDepartmentFilter(dept);
    loadEmployees({
      job_title: jt || undefined,
      department: dept || undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply when URL filter params change
  }, [searchParams]);

  const handleSearch = () => {
    loadEmployees();
  };

  const handleActiveFilter = (value) => {
    setActiveFilter(value);
  };

  const openNew = () => {
    setFormData(emptyForm());
    setDialogMode('new');
    setDialogOpen(true);
  };

  const openEdit = () => {
    const e = detailEmployee || selectedEmployee;
    if (!e) return;
    setFormData({
      employee_number: e.employee_number != null ? String(e.employee_number) : '',
      first_name: e.first_name ?? '',
      last_name: e.last_name ?? '',
      email: e.email ?? '',
      password: '',
      phone: e.phone ?? '',
      role: e.role ?? 'employee',
      is_active: e.is_active !== false,
      date_of_hire: formatDateForInput(e.date_of_hire),
      job_title: e.job_title ?? '',
      department: e.department ?? '',
      current_pay_rate: e.current_pay_rate != null ? String(e.current_pay_rate) : '',
      kiosk_pin: e.kiosk_pin ?? '',
      supervisor_id: e.supervisor_id != null ? String(e.supervisor_id) : '',
      address_line1: e.address_line1 ?? '',
      address_line2: e.address_line2 ?? '',
      city: e.city ?? '',
      state: e.state ?? '',
      zip_code: e.zip_code ?? '',
      requires_training: e.requires_training !== false,
      data_collection: e.data_collection === true,
      scoped_can_use_existing_customers: e.scoped_can_use_existing_customers !== false,
      scoped_see_others_documents: e.scoped_see_others_documents === true
    });
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const openCopy = () => {
    const e = detailEmployee || selectedEmployee;
    if (!e) return;
    setFormData({
      employee_number: '',
      first_name: e.first_name ?? '',
      last_name: e.last_name ?? '',
      email: '',
      password: '',
      kiosk_pin: '',
      supervisor_id: e.supervisor_id != null ? String(e.supervisor_id) : '',
      phone: e.phone ?? '',
      role: e.role ?? 'employee',
      is_active: true,
      date_of_hire: formatDateForInput(e.date_of_hire),
      job_title: e.job_title ?? '',
      department: e.department ?? '',
      current_pay_rate: e.current_pay_rate != null ? String(e.current_pay_rate) : '',
      address_line1: e.address_line1 ?? '',
      address_line2: e.address_line2 ?? '',
      city: e.city ?? '',
      state: e.state ?? '',
      zip_code: e.zip_code ?? '',
      requires_training: e.requires_training !== false,
      data_collection: e.data_collection === true,
      scoped_can_use_existing_customers: e.scoped_can_use_existing_customers !== false,
      scoped_see_others_documents: e.scoped_see_others_documents === true
    });
    setDialogMode('copy');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const { first_name, last_name, email } = formData;
    if (!first_name?.trim() || !last_name?.trim() || !email?.trim()) {
      setError('First name, last name, and email are required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (dialogMode === 'edit' && selectedEmployee?.id) {
        const payload = { ...formData };
        delete payload.password;
        if (payload.employee_number === '' || payload.employee_number == null) {
          delete payload.employee_number;
        } else {
          payload.employee_number = parseInt(payload.employee_number, 10);
        }
        if (payload.supervisor_id === '' || payload.supervisor_id == null) {
          payload.supervisor_id = null;
        } else {
          payload.supervisor_id = parseInt(payload.supervisor_id, 10);
        }
        await employeeService.update(selectedEmployee.id, payload);
        const res = await employeeService.getById(selectedEmployee.id);
        setDetailEmployee(res.data ?? null);
        await loadEmployees();
      } else {
        const payload = { ...formData };
        if (payload.employee_number === '' || payload.employee_number == null) {
          delete payload.employee_number;
        } else {
          payload.employee_number = parseInt(payload.employee_number, 10);
        }
        if (payload.supervisor_id === '' || payload.supervisor_id == null) {
          payload.supervisor_id = null;
        } else {
          payload.supervisor_id = parseInt(payload.supervisor_id, 10);
        }
        await employeeService.create(payload);
        await loadEmployees();
      }
      setDialogOpen(false);
    } catch (err) {
      console.error('Error saving employee:', err);
      setError(err.response?.data?.message || 'Failed to save employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => setDeleteDialogOpen(true);

  const handleDeleteConfirm = async () => {
    if (!selectedEmployee?.id) return;
    try {
      setDeleting(true);
      setError('');
      await employeeService.delete(selectedEmployee.id);
      setDeleteDialogOpen(false);
      handleClearSelection();
      await loadEmployees();
    } catch (err) {
      console.error('Error deleting employee:', err);
      setError(err.response?.data?.message || 'Failed to delete employee');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrint = () => {
    const employeeNumber = selectedEmployee?.employee_number;
    printWithFilename(employeeNumber ? `Employee-${employeeNumber}` : 'Employees');
  };

  const handlePrintAllEmployees = async () => {
    setPrintMenuAnchor(null);
    try {
      setPrintListLoading(true);
      setError('');
      const all = await fetchAllEmployeesUnfiltered();
      setEmployeesForPrintList(all);
      await new Promise((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
      });
      printWithFilename('Employees-All');
      let cleared = false;
      const cleanup = () => {
        if (cleared) return;
        cleared = true;
        setEmployeesForPrintList(null);
        window.removeEventListener('afterprint', cleanup);
        window.clearTimeout(fallbackTimer);
      };
      const fallbackTimer = window.setTimeout(cleanup, 90_000);
      window.addEventListener('afterprint', cleanup);
    } catch (err) {
      console.error('Error loading employees for print:', err);
      setError(err.response?.data?.message || 'Failed to load full employee list for printing');
      setEmployeesForPrintList(null);
    } finally {
      setPrintListLoading(false);
    }
  };

  const handleEmployeeRowClick = async (emp) => {
    setSelectedEmployee(emp);
    setDetailEmployee(null);
    setDetailLoading(true);
    try {
      const res = await employeeService.getById(emp.id);
      setDetailEmployee(res.data ?? null);
    } catch (err) {
      console.error('Error loading employee detail:', err);
      setError(err.response?.data?.message || 'Failed to load employee');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshSelectedDetail = async () => {
    const id = selectedEmployee?.id || detailEmployee?.id;
    if (!id) return;
    const res = await employeeService.getById(id);
    const data = res.data ?? null;
    setDetailEmployee(data);
    if (data) {
      setSelectedEmployee((prev) => (prev ? { ...prev, ...data } : data));
      setEmployees((list) =>
        list.map((emp) => (emp.id === data.id ? { ...emp, current_pay_rate: data.current_pay_rate } : emp))
      );
    }
  };

  const openPayHistoryDialog = () => {
    setPayHistoryForm({
      effective_date: formatLocalTodayYmd(),
      rate: detailEmployee?.current_pay_rate != null ? String(detailEmployee.current_pay_rate) : '',
      rate_type: 'REGULAR'
    });
    setHistoryDialog('pay');
  };

  const openWorkHistoryDialog = () => {
    setWorkHistoryForm({
      effective_date: formatLocalTodayYmd(),
      notes: ''
    });
    setHistoryDialog('work');
  };

  const openNoteDialog = () => {
    setNoteForm({
      note_date: formatLocalTodayYmd(),
      note_text: ''
    });
    setHistoryDialog('note');
  };

  const handleSaveHistoryDialog = async () => {
    const employeeId = detailEmployee?.id;
    if (!employeeId || !historyDialog) return;
    try {
      setHistorySaving(true);
      setError('');
      if (historyDialog === 'pay') {
        if (!payHistoryForm.effective_date || payHistoryForm.rate === '' || payHistoryForm.rate == null) {
          setError('Pay raise requires an effective date and rate');
          return;
        }
        await employeeService.addPayHistory(employeeId, {
          effective_date: payHistoryForm.effective_date,
          rate: payHistoryForm.rate,
          rate_type: payHistoryForm.rate_type || 'REGULAR'
        });
      } else if (historyDialog === 'work') {
        if (!workHistoryForm.effective_date) {
          setError('Work history requires an effective date');
          return;
        }
        await employeeService.addWorkHistory(employeeId, {
          effective_date: workHistoryForm.effective_date,
          notes: workHistoryForm.notes
        });
      } else if (historyDialog === 'note') {
        if (!String(noteForm.note_text || '').trim()) {
          setError('Note text is required');
          return;
        }
        await employeeService.addNote(employeeId, {
          note_date: noteForm.note_date || formatLocalTodayYmd(),
          note_text: noteForm.note_text
        });
      }
      setHistoryDialog(null);
      await refreshSelectedDetail();
    } catch (err) {
      console.error('Error saving history entry:', err);
      setError(err.response?.data?.message || 'Failed to save entry');
    } finally {
      setHistorySaving(false);
    }
  };

  const handleDeletePayHistory = async (entryId) => {
    if (!detailEmployee?.id || !entryId) return;
    if (!window.confirm('Delete this pay history entry?')) return;
    try {
      setError('');
      await employeeService.deletePayHistory(detailEmployee.id, entryId);
      await refreshSelectedDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete pay history');
    }
  };

  const handleDeleteWorkHistory = async (entryId) => {
    if (!detailEmployee?.id || !entryId) return;
    if (!window.confirm('Delete this work history entry?')) return;
    try {
      setError('');
      await employeeService.deleteWorkHistory(detailEmployee.id, entryId);
      await refreshSelectedDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete work history');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!detailEmployee?.id || !noteId) return;
    if (!window.confirm('Delete this note?')) return;
    try {
      setError('');
      await employeeService.deleteNote(detailEmployee.id, noteId);
      await refreshSelectedDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete note');
    }
  };

  useEffect(() => {
    if (didApplyFocusFromUrl.current || focusEmployeeIdValid == null || loading) return;
    const match = employees.find(
      (emp) => emp.id === focusEmployeeIdValid || String(emp.id) === String(focusEmployeeIdValid)
    );
    if (match) {
      didApplyFocusFromUrl.current = true;
      handleEmployeeRowClick(match);
      return;
    }
    if (employees.length === 0) return;
    didApplyFocusFromUrl.current = true;
    employeeService.getById(focusEmployeeIdValid)
      .then((res) => {
        const emp = res.data;
        if (emp) handleEmployeeRowClick(emp);
      })
      .catch((err) => {
        console.error('Error loading employee from URL:', err);
      });
  }, [focusEmployeeIdValid, employees, loading]);

  const handleClearSelection = () => {
    setSelectedEmployee(null);
    setDetailEmployee(null);
  };

  const fullName = (e) =>
    [e?.first_name, e?.last_name].filter(Boolean).join(' ') || '—';

  const formatDate = formatDateDisplay;

  const getEmployeeExportData = useCallback(() => {
    if (!employees.length) return null;
    const headers = showPayRateColumn
      ? ['Employee Name', 'Employee ID', 'Status', 'Hire Date', '90-Day Date', 'Job Title', 'Dept', 'Pay Rate']
      : ['Employee Name', 'Employee ID', 'Status', 'Hire Date', '90-Day Date', 'Job Title', 'Dept'];
    const rows = employees.map((emp) => {
      const row = [
        fullName(emp),
        emp.employee_number != null ? String(emp.employee_number) : '',
        emp.is_active ? 'Active' : 'Inactive',
        formatDateDisplay(emp.date_of_hire) || '',
        formatNinetyDayDate(emp.date_of_hire),
        emp.job_title || (emp.role ? String(emp.role).replace(/_/g, ' ') : '') || '',
        emp.department || ''
      ];
      if (showPayRateColumn) {
        row.push(formatCurrency(emp.current_pay_rate));
      }
      return row;
    });
    const filterSummary = [
      activeFilter === 'active' ? 'Active only' : 'All statuses',
      jobTitleFilter?.trim() && `Job title: ${jobTitleFilter.trim()}`,
      departmentFilter?.trim() && `Dept: ${departmentFilter.trim()}`,
      (hireDateFrom || hireDateTo) && `Hire ${hireDateFrom || '…'}–${hireDateTo || '…'}`,
      searchTerm?.trim() && `Search: ${searchTerm.trim()}`
    ]
      .filter(Boolean)
      .join(' · ');
    const footer = Array(headers.length).fill('');
    footer[0] = `Total: ${employees.length}`;
    if (headers.length > 1) footer[1] = filterSummary;
    const pdfTitle = `Employees — ${activeFilter === 'active' ? 'active' : 'all'}${filterSummary ? ` (${filterSummary})` : ''}`;
    return {
      headers,
      rows,
      footer,
      pdfTitle: pdfTitle.slice(0, 120),
      filenameBase: `employees-${activeFilter}-${new Date().toISOString().slice(0, 10)}`,
      pdfLandscape: showPayRateColumn
    };
  }, [
    employees,
    showPayRateColumn,
    activeFilter,
    jobTitleFilter,
    departmentFilter,
    hireDateFrom,
    hireDateTo,
    searchTerm
  ]);

  return (
    <Box>
      <Box className="no-print-hr">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Human Resources
        </Typography>
        <Box>
          <IconButton size="small" color="primary" title="Edit" disabled={!selectedEmployee} onClick={openEdit}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" title="Delete" disabled={!selectedEmployee} onClick={handleDeleteClick}>
            <Delete fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Copy" disabled={!selectedEmployee} onClick={openCopy}>
            <ContentCopy fontSize="small" />
          </IconButton>
          <IconButton size="small" color="primary" title="Print" onClick={(e) => setPrintMenuAnchor(e.currentTarget)}>
            <Print fontSize="small" />
          </IconButton>
          <Menu anchorEl={printMenuAnchor} open={!!printMenuAnchor} onClose={() => setPrintMenuAnchor(null)} disableEnforceFocus disableRestoreFocus>
            <MenuItem onClick={handlePrintAllEmployees} disabled={printListLoading}>
              {printListLoading ? 'Loading full list…' : 'Print employee list (all)'}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handlePrint();
                setPrintMenuAnchor(null);
              }}
              disabled={!selectedEmployee}
            >
              Print selected employee
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Employee Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main' }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee Name</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedEmployee ? fullName(detailEmployee || selectedEmployee) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee ID</Typography>
            <Typography variant="body2">{(detailEmployee || selectedEmployee)?.employee_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {(detailEmployee || selectedEmployee) ? (
              <Chip
                label={(detailEmployee || selectedEmployee).is_active ? 'Active' : 'Inactive'}
                color={(detailEmployee || selectedEmployee).is_active ? 'success' : 'default'}
                size="small"
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Training</Typography>
            {(detailEmployee || selectedEmployee) ? (
              <Chip
                label={(detailEmployee || selectedEmployee).requires_training !== false ? 'Sign-off required' : 'Excluded'}
                color={(detailEmployee || selectedEmployee).requires_training !== false ? 'primary' : 'default'}
                size="small"
                variant={(detailEmployee || selectedEmployee).requires_training !== false ? 'filled' : 'outlined'}
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Data Collection</Typography>
            {(detailEmployee || selectedEmployee) ? (
              <Chip
                label={(detailEmployee || selectedEmployee).data_collection ? 'On Clocked In' : 'Not on roster'}
                color={(detailEmployee || selectedEmployee).data_collection ? 'success' : 'default'}
                size="small"
                variant={(detailEmployee || selectedEmployee).data_collection ? 'filled' : 'outlined'}
              />
            ) : (
              <Typography variant="body2">—</Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Hire Date</Typography>
            <Typography variant="body2">{selectedEmployee ? (formatDate((detailEmployee || {}).date_of_hire) || '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>90-Day Date</Typography>
            <Typography variant="body2">
              {selectedEmployee ? formatNinetyDayDate((detailEmployee || selectedEmployee)?.date_of_hire) : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Job Title</Typography>
            <Typography variant="body2">{selectedEmployee ? ((detailEmployee || {}).job_title || (selectedEmployee?.role ? String(selectedEmployee.role).replace(/_/g, ' ') : '') || '—') : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Dept</Typography>
            <Typography variant="body2">{(detailEmployee || selectedEmployee)?.department ?? '—'}</Typography>
          </Box>
          {showPayRateColumn && (
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Pay Rate</Typography>
              <Typography variant="body2">{(detailEmployee || selectedEmployee) ? formatCurrency((detailEmployee || {}).current_pay_rate) : '—'}</Typography>
            </Box>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={handleClearSelection} disabled={!selectedEmployee} sx={{ ml: 'auto' }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Search and filters */}
      <Paper sx={{ p: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" color="success" size="small" startIcon={<AddIcon />} onClick={openNew}>
            New Employee
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterList fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Status:</Typography>
            <Chip
              label="Active"
              size="small"
              onClick={() => handleActiveFilter('active')}
              color={activeFilter === 'active' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="All"
              size="small"
              onClick={() => handleActiveFilter('all')}
              color={activeFilter === 'all' ? 'primary' : 'default'}
              sx={{ cursor: 'pointer' }}
            />
          </Box>
          <TextField
            size="small"
            placeholder="Job title"
            value={jobTitleFilter}
            onChange={(e) => setJobTitleFilter(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 140 }}
            InputProps={{ sx: { '& input': { py: 0.5 } } }}
          />
          <TextField
            size="small"
            placeholder="Department"
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ width: 120 }}
            InputProps={{ sx: { '& input': { py: 0.5 } } }}
          />
          <TextField
            size="small"
            label="Hire from"
            type="date"
            value={hireDateFrom}
            onChange={(e) => setHireDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130 }}
            inputProps={{ 'aria-label': 'Hire date from' }}
          />
          <TextField
            size="small"
            label="Hire to"
            type="date"
            value={hireDateTo}
            onChange={(e) => setHireDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130 }}
            inputProps={{ 'aria-label': 'Hire date to' }}
          />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            Showing Employee Record(s){activeFilter === 'all' ? ' (active + inactive)' : ''}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPayRateColumn}
                onChange={(e) => {
                  const next = e.target.checked;
                  setShowPayRateColumn(next);
                  try {
                    localStorage.setItem(HR_PAY_RATE_COLUMN_KEY, next ? '1' : '0');
                  } catch {
                    /* ignore */
                  }
                }}
                inputProps={{ 'aria-label': 'Show pay rate information in list, summary, and detail' }}
              />
            }
            label={<Typography variant="body2">Show pay rates</Typography>}
            sx={{ mr: 0, whiteSpace: 'nowrap' }}
          />
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end" size="small">
                    <Search />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260 }}
          />
          <ReportExportMenu
            disabled={loading || employees.length === 0}
            getData={getEmployeeExportData}
            defaultFilenameBase="employees"
            buttonLabel="Export"
          />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Employee Navigator */}
        <Grid item xs={12} md={7}>
          <TableContainer component={Paper} sx={{ maxHeight: '60vh' }}>
            <Table
              size="small"
              stickyHeader
              sx={{
                '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
                  top: -13,
                  zIndex: 20,
                  backgroundColor: 'background.paper',
                  backgroundImage: 'none',
                  backgroundClip: 'padding-box',
                  opacity: 1,
                  overflow: 'hidden',
                  borderTop: '1px solid',
                  borderTopColor: 'background.paper',
                  boxShadow: (theme) =>
                    `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee Name</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee ID</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Status</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Hire Date</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>90-Day Date</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Job Title</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Dept</Typography></TableCell>
                  {showPayRateColumn && (
                    <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Pay Rate</Typography></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={employeeTableColCount} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={employeeTableColCount} align="center">
                      <Typography variant="body2" color="textSecondary">No employees found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp) => (
                    <TableRow
                      key={emp.id}
                      hover
                      onClick={() => handleEmployeeRowClick(emp)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedEmployee?.id === emp.id ? 'action.selected' : 'inherit'
                      }}
                    >
                      <TableCell>{fullName(emp)}</TableCell>
                      <TableCell>{emp.employee_number ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={emp.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={emp.is_active ? 'success' : 'default'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(emp.date_of_hire)}</TableCell>
                      <TableCell>{formatNinetyDayDate(emp.date_of_hire)}</TableCell>
                      <TableCell>{emp.job_title || (emp.role ? String(emp.role).replace(/_/g, ' ') : '') || '—'}</TableCell>
                      <TableCell>{emp.department || '—'}</TableCell>
                      {showPayRateColumn && (
                        <TableCell>{formatCurrency(emp.current_pay_rate)}</TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Employee Inquiry + Payroll */}
        <Grid item xs={12} md={5}>
          {!selectedEmployee ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Employee Inquiry</Typography>
              <Typography variant="body2" color="textSecondary">
                No employee selected. Select an employee from the list to view details and payroll.
              </Typography>
            </Paper>
          ) : detailLoading ? (
            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={24} />
            </Paper>
          ) : !detailEmployee ? (
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Could not load employee details. Select again or try later.
              </Typography>
            </Paper>
          ) : (
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AccountBalance fontSize="small" color="action" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {showPayRateColumn ? 'Payroll section' : 'Employee details'} — {fullName(detailEmployee)}
                  </Typography>
                  <IconButton size="small" color="primary" title="Edit employee" onClick={openEdit} sx={{ ml: 0.5 }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  {showPayRateColumn
                    ? 'Date of hire, work history, pay raises over time, and notes/history.'
                    : 'Date of hire, work history, and notes. Turn on Show pay rates to view compensation details.'}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={showPayRateColumn ? 6 : 12}>
                    <Typography variant="caption" color="text.secondary" display="block">Date of hire</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(detailEmployee?.date_of_hire)}
                    </Typography>
                  </Grid>
                  {showPayRateColumn && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">Current rate of pay</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(detailEmployee?.current_pay_rate)}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block">Address</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {(() => {
                        const d = detailEmployee;
                        const line1 = [d?.address_line1, d?.address_line2].filter(Boolean).join(', ');
                        const line2 = [d?.city, [d?.state, d?.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                        return line1 || line2 ? [line1, line2].filter(Boolean).join(' — ') : '—';
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Work history (rehire dates)</Typography>
                      <Button size="small" onClick={openWorkHistoryDialog} sx={{ textTransform: 'none', minWidth: 0, py: 0 }}>
                        Add
                      </Button>
                    </Box>
                    {!detailEmployee?.workHistory?.length ? (
                      <Typography variant="body2" color="text.secondary">No work history yet. Add rehire or leave/return dates.</Typography>
                    ) : (
                      <List dense disablePadding>
                        {(detailEmployee.workHistory || []).map((entry, i) => (
                          <ListItem
                            key={entry.id || i}
                            disablePadding
                            secondaryAction={
                              entry.id ? (
                                <IconButton edge="end" size="small" title="Delete" onClick={() => handleDeleteWorkHistory(entry.id)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              ) : null
                            }
                          >
                            <ListItemText
                              primary={formatDate(entry.effective_date)}
                              secondary={entry.notes || undefined}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                  {showPayRateColumn && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Pay history (raises & rate changes)</Typography>
                        <Button size="small" onClick={openPayHistoryDialog} sx={{ textTransform: 'none', minWidth: 0, py: 0 }}>
                          Add raise
                        </Button>
                      </Box>
                      {!detailEmployee?.payHistory?.length ? (
                        <Typography variant="body2" color="text.secondary">No pay history yet. Add a raise to start the progression track.</Typography>
                      ) : (
                        <List dense disablePadding>
                          {(detailEmployee.payHistory || []).map((entry, i) => (
                            <ListItem
                              key={entry.id || i}
                              disablePadding
                              secondaryAction={
                                entry.id ? (
                                  <IconButton edge="end" size="small" title="Delete" onClick={() => handleDeletePayHistory(entry.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                ) : null
                              }
                            >
                              <ListItemText
                                primary={`${formatDate(entry.effective_date)} — ${formatCurrency(entry.rate)}`}
                                secondary={entry.rate_type || 'REGULAR'}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">Notes / history</Typography>
                      <Button size="small" onClick={openNoteDialog} sx={{ textTransform: 'none', minWidth: 0, py: 0 }}>
                        Add note
                      </Button>
                    </Box>
                    {!detailEmployee?.notes?.length ? (
                      <Typography variant="body2" color="text.secondary">No notes yet.</Typography>
                    ) : (
                      <List dense disablePadding>
                        {(detailEmployee.notes || []).map((entry, i) => {
                          const by = [entry.created_by_first_name, entry.created_by_last_name].filter(Boolean).join(' ');
                          return (
                            <ListItem
                              key={entry.id || i}
                              disablePadding
                              alignItems="flex-start"
                              secondaryAction={
                                entry.id ? (
                                  <IconButton edge="end" size="small" title="Delete" onClick={() => handleDeleteNote(entry.id)}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                ) : null
                              }
                            >
                              <ListItemText
                                primary={formatDate(entry.note_date)}
                                secondary={
                                  <>
                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>
                                      {entry.note_text}
                                    </Typography>
                                    {by ? (
                                      <Typography variant="caption" color="text.secondary" component="span">
                                        Logged by {by}
                                      </Typography>
                                    ) : null}
                                  </>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Grid>
                </Grid>
                {showPayRateColumn && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Adding a REGULAR raise also updates Current rate of pay to the latest REGULAR entry.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Employee form dialog (New / Edit / Copy) */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'new' ? 'New Employee' : dialogMode === 'edit' ? 'Edit Employee' : 'Copy Employee'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={dialogMode === 'edit' ? 'Employee ID # (change for legacy — must be unique)' : 'Employee ID # (optional — leave blank to auto-assign)'}
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={formData.employee_number}
                onChange={(e) => setFormData({ ...formData, employee_number: e.target.value })}
                placeholder="Auto"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="First name" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Last name" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
            </Grid>
            {(dialogMode === 'new' || dialogMode === 'copy') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Password (leave blank for default)" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="ChangeMe123!" inputProps={{ autoComplete: 'new-password' }} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Address</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address line 1" value={formData.address_line1} onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Address line 2" value={formData.address_line2} onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="City" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="State" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="e.g. CA" inputProps={{ maxLength: 2 }} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" label="ZIP" value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Supervisor</InputLabel>
                <Select
                  value={formData.supervisor_id || ''}
                  label="Supervisor"
                  onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {employees
                    .filter((emp) => emp.is_active !== false)
                    .filter((emp) => dialogMode !== 'edit' || emp.id !== (detailEmployee || selectedEmployee)?.id)
                    .map((emp) => (
                      <MenuItem key={emp.id} value={String(emp.id)}>
                        {emp.first_name} {emp.last_name}
                        {emp.employee_number ? ` (#${emp.employee_number})` : ''}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select value={formData.role} label="Role" onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="sales_rep">Outside Sales Rep</MenuItem>
                  <MenuItem value="estimator">Estimator (scoped quotes)</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {canManageEnterpriseAdmins ? (
                    <MenuItem value="enterprise_admin">Enterprise Admin</MenuItem>
                  ) : (
                    formData.role === 'enterprise_admin' ? (
                      <MenuItem value="enterprise_admin" disabled>Enterprise Admin (requires Enterprise Admin)</MenuItem>
                    ) : null
                  )}
                </Select>
              </FormControl>
            </Grid>
            {formData.role === 'estimator' && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.scoped_can_use_existing_customers !== false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            scoped_can_use_existing_customers: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Can use existing customers (off = only customers they create/source)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.scoped_see_others_documents === true}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            scoped_see_others_documents: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Can see others' quotes / orders / packing slips"
                  />
                </Grid>
              </>
            )}
            {dialogMode === 'edit' && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={formData.is_active} label="Status" onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}>
                    <MenuItem value={true}>Active</MenuItem>
                    <MenuItem value={false}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.requires_training !== false}
                    onChange={(e) => setFormData({ ...formData, requires_training: e.target.checked })}
                  />
                }
                label="Required to sign training / onboarding documents"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.data_collection === true}
                    onChange={(e) => setFormData({ ...formData, data_collection: e.target.checked })}
                  />
                }
                label="Data Collection — show on Clocked In page (even with no punches)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Date of hire"
                type="date"
                value={formData.date_of_hire}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData({ ...formData, date_of_hire: v });
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: '2099-12-31' }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Job title" value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Current pay rate" type="number" inputProps={{ step: 0.01, min: 0 }} value={formData.current_pay_rate} onChange={(e) => setFormData({ ...formData, current_pay_rate: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Kiosk PIN (for punching in/out)" value={formData.kiosk_pin} onChange={(e) => setFormData({ ...formData, kiosk_pin: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="e.g. 123" inputProps={{ maxLength: 10 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete employee?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will set the employee to inactive. They will no longer appear in the active list. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyDialog != null} onClose={() => !historySaving && setHistoryDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {historyDialog === 'pay' ? 'Add pay raise' : historyDialog === 'work' ? 'Add work history' : 'Add note'}
        </DialogTitle>
        <DialogContent>
          {historyDialog === 'pay' && (
            <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Effective date"
                  type="date"
                  value={payHistoryForm.effective_date}
                  onChange={(e) => setPayHistoryForm((p) => ({ ...p, effective_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="New rate"
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={payHistoryForm.rate}
                  onChange={(e) => setPayHistoryForm((p) => ({ ...p, rate: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rate type</InputLabel>
                  <Select
                    label="Rate type"
                    value={payHistoryForm.rate_type}
                    onChange={(e) => setPayHistoryForm((p) => ({ ...p, rate_type: e.target.value }))}
                  >
                    {PAY_RATE_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          {historyDialog === 'work' && (
            <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Effective date"
                  type="date"
                  value={workHistoryForm.effective_date}
                  onChange={(e) => setWorkHistoryForm((p) => ({ ...p, effective_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  multiline
                  minRows={2}
                  value={workHistoryForm.notes}
                  onChange={(e) => setWorkHistoryForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="e.g. Rehired, returned from leave"
                />
              </Grid>
            </Grid>
          )}
          {historyDialog === 'note' && (
            <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Date"
                  type="date"
                  value={noteForm.note_date}
                  onChange={(e) => setNoteForm((p) => ({ ...p, note_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  multiline
                  minRows={3}
                  value={noteForm.note_text}
                  onChange={(e) => setNoteForm((p) => ({ ...p, note_text: e.target.value }))}
                  required
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(null)} disabled={historySaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveHistoryDialog} disabled={historySaving}>
            {historySaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      </Box>

      {/* Print view – full roster (all employees, unfiltered) */}
      {employeesForPrintList != null && (
        <PrintPage title="Employee roster" showSpacer={false}>
          <div className="print-section" style={{ marginBottom: 12 }}>
            <strong>All employees</strong> (active and inactive, not limited by screen filters).{' '}
            <span style={{ color: '#444' }}>Total: {employeesForPrintList.length}</span>
          </div>
          {employeesForPrintList.length === 0 ? (
            <p style={{ fontSize: 13 }}>No employees in the system.</p>
          ) : (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Employee name</th>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Hire date</th>
                  <th>90-day date</th>
                  <th>Job title</th>
                  <th>Dept</th>
                  {showPayRateColumn && <th>Pay rate</th>}
                </tr>
              </thead>
              <tbody>
                {employeesForPrintList.map((emp) => (
                  <tr key={emp.id}>
                    <td>{fullName(emp)}</td>
                    <td>{emp.employee_number ?? '—'}</td>
                    <td>{emp.is_active ? 'Active' : 'Inactive'}</td>
                    <td>{formatDateDisplay(emp.date_of_hire) || '—'}</td>
                    <td>{formatNinetyDayDate(emp.date_of_hire)}</td>
                    <td>{emp.job_title || (emp.role ? String(emp.role).replace(/_/g, ' ') : '') || '—'}</td>
                    <td>{emp.department || '—'}</td>
                    {showPayRateColumn && <td>{formatCurrency(emp.current_pay_rate)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </PrintPage>
      )}

      {/* Print view – selected employee summary */}
      {(detailEmployee || selectedEmployee) && employeesForPrintList == null && (
        <PrintPage title="Employee Inquiry" showSpacer={false}>
          <div className="print-quote-details-box" style={{ marginBottom: 16 }}>
            <div className="print-quote-details-left">
              <div>
                <strong>Employee:</strong> {fullName(detailEmployee || selectedEmployee)}
              </div>
              <div>
                <strong>ID:</strong> {(detailEmployee || selectedEmployee).employee_number ?? '—'}
              </div>
            </div>
            <div className="print-quote-details-right">
              <div>
                <strong>Status:</strong>{' '}
                {(detailEmployee || selectedEmployee).is_active ? 'Active' : 'Inactive'}
              </div>
            </div>
          </div>
          <div className="print-section">
            <strong>Hire date:</strong> {formatDate((detailEmployee || {}).date_of_hire)} &emsp;{' '}
            <strong>90-day date:</strong> {formatNinetyDayDate((detailEmployee || selectedEmployee).date_of_hire)} &emsp;{' '}
            <strong>Job title:</strong> {(detailEmployee || {}).job_title || '—'} &emsp;{' '}
            <strong>Dept:</strong> {(detailEmployee || {}).department || '—'}
            {showPayRateColumn && (
              <>
                {' '}
                &emsp; <strong>Pay rate:</strong> {formatCurrency((detailEmployee || {}).current_pay_rate)}
              </>
            )}
          </div>
          <div className="print-section">
            <strong>Email:</strong> {(detailEmployee || selectedEmployee).email ?? '—'} &emsp;{' '}
            <strong>Phone:</strong> {(detailEmployee || selectedEmployee).phone ?? '—'}
          </div>
          {((detailEmployee || selectedEmployee).address_line1 ||
            (detailEmployee || selectedEmployee).city) && (
            <div className="print-section">
              <strong>Address:</strong>{' '}
              {[(detailEmployee || selectedEmployee).address_line1,
              (detailEmployee || selectedEmployee).address_line2]
                .filter(Boolean)
                .join(', ')}
              {((detailEmployee || selectedEmployee).city ||
                (detailEmployee || selectedEmployee).state ||
                (detailEmployee || selectedEmployee).zip_code) &&
                ` — ${[
                  (detailEmployee || selectedEmployee).city,
                  [
                    (detailEmployee || selectedEmployee).state,
                    (detailEmployee || selectedEmployee).zip_code
                  ]
                    .filter(Boolean)
                    .join(' ')
                ]
                  .filter(Boolean)
                  .join(', ')}`}
            </div>
          )}
        </PrintPage>
      )}
    </Box>
  );
};

export default HumanResources;
